import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import PanelAdmin from './pages/PanelAdmin';
import ProfesorLayout from './components/ProfesorLayout';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SelectedCourseProvider } from './contexts/SelectedCourseContext';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const AppRoutes: React.FC = () => {
  const { isAuthenticated, userRole, loading } = useAuth();

  // Mostrar loading mientras se verifica la sesión
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Rutas públicas */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />

      {/* Rutas protegidas de administrador */}
      <Route
        path="/admin/*"
        element={
          isAuthenticated && (userRole === 'admin' || userRole === 'jtp' || userRole === 'ayudante') ? (
            <PanelAdmin />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      {/* Rutas protegidas de profesor */}
      <Route
        path="/profesor/*"
        element={
          isAuthenticated && userRole === 'profesor' ? (
            <ProfesorLayout />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      {/* Redirección por defecto */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <SelectedCourseProvider>
            <Router>
              <div className="App">
                <AppRoutes />
              </div>
            </Router>
          </SelectedCourseProvider>
        </AuthProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
